'use client';

import { useState, useEffect, useCallback, useRef } from 'react';

const PREFIX = 'beast:';

// Global counter for unique hook instance IDs
let instanceCounter = 0;

// Debounced localStorage writes — prevents main thread blocking on large JSON serialization
const pendingWrites = new Map<string, { value: string; timer: ReturnType<typeof setTimeout> }>();
function debouncedStorageWrite(key: string, json: string, delayMs = 300) {
  const existing = pendingWrites.get(key);
  if (existing) clearTimeout(existing.timer);
  pendingWrites.set(key, {
    value: json,
    timer: setTimeout(() => {
      try { localStorage.setItem(key, json); } catch (e) {
        // Never swallow quota errors silently — a full quota breaks
        // persistence app-wide (active project, settings) while looking
        // like everything saved fine.
        console.error(`[beast] localStorage write FAILED for ${key}:`, e);
      }
      pendingWrites.delete(key);
    }, delayMs),
  });
}
// Flush all pending writes (e.g., before page unload)
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    for (const [key, { value }] of pendingWrites) {
      try { localStorage.setItem(key, value); } catch {}
    }
    pendingWrites.clear();
  });
}

/**
 * useState that persists to localStorage.
 * Survives navigation, page reloads, and browser restarts.
 *
 * Cross-component sync: when one hook instance writes, others with the
 * same key pick up the change via a custom event. Each instance tags its
 * writes so it never re-reads its own update (which caused a race where
 * the RAF-deferred sync event could overwrite state with stale data).
 */
export function usePersistedState<T>(key: string, defaultValue: T): [T, (value: T | ((prev: T) => T)) => void] {
  const fullKey = PREFIX + key;

  // Stable instance ID — survives re-renders, unique per hook call
  const instanceId = useRef(`ps_${++instanceCounter}`);

  // Always start with defaultValue for SSR/hydration match
  const [value, setValueRaw] = useState<T>(defaultValue);
  const [hydrated, setHydrated] = useState(false);

  // Track previous key so we can reset state when key changes
  const prevKeyRef = useRef(fullKey);

  // Tracks which fullKey has actually been hydrated from localStorage.
  // Writes are gated on this matching the current fullKey — prevents
  // pre-hydration writes (and stale-closure writes during key transitions)
  // from clobbering localStorage with empty/wrong-key data. Critical for
  // chat history when the chat key depends on activeProject (which hydrates
  // async via this same hook) — the brief window where chatKey points at
  // "general" before activeProject loads must not write through.
  const hydratedKeyRef = useRef<string | null>(null);

  // Load from localStorage after hydration (or when key changes)
  useEffect(() => {
    const keyChanged = prevKeyRef.current !== fullKey;
    prevKeyRef.current = fullKey;

    try {
      const stored = localStorage.getItem(fullKey);
      if (stored !== null) {
        setValueRaw(JSON.parse(stored) as T);
      } else if (keyChanged) {
        // Key changed and no data for new key — reset to default
        // (prevents leaking old key's data into the new key's view)
        setValueRaw(defaultValue);
      }
    } catch {
      if (keyChanged) setValueRaw(defaultValue);
    }
    hydratedKeyRef.current = fullKey;
    setHydrated(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullKey]);

  // Listen for cross-component sync events (from OTHER hook instances only)
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      // Skip events from this same hook instance — we already have the value
      if (detail?.key === fullKey && detail?.source !== instanceId.current) {
        // Prefer the value carried in the event — localStorage writes are
        // debounced, so reading from storage here can return stale data and
        // overwrite the just-set value (caused project picker selections to
        // visually revert to General).
        if ('value' in (detail || {})) {
          try {
            setValueRaw(detail.value === null ? defaultValue : (JSON.parse(detail.value) as T));
          } catch {}
          return;
        }
        try {
          const stored = localStorage.getItem(fullKey);
          if (stored !== null) {
            setValueRaw(JSON.parse(stored) as T);
          } else {
            setValueRaw(defaultValue);
          }
        } catch {}
      }
    };
    window.addEventListener('beast:state-sync', handler);
    return () => window.removeEventListener('beast:state-sync', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullKey]);

  // Also listen for cross-tab sync via native storage event
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === fullKey) {
        try {
          if (e.newValue !== null) {
            setValueRaw(JSON.parse(e.newValue) as T);
          } else {
            setValueRaw(defaultValue);
          }
        } catch {}
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullKey]);

  const setValue = useCallback((update: T | ((prev: T) => T)) => {
    let serialized: string | null = null;
    setValueRaw((prev) => {
      const next = typeof update === 'function' ? (update as (prev: T) => T)(prev) : update;
      // Only persist if we have actually hydrated THIS key. Writes during
      // the pre-hydration window (or aimed at a stale key from a previous
      // render's closure) would clobber saved data with [] / wrong-namespace
      // values. Skip them — the next legitimate write will catch up.
      try {
        serialized = JSON.stringify(next);
      } catch {
        serialized = null;
      }
      if (hydratedKeyRef.current === fullKey && serialized !== null) {
        debouncedStorageWrite(fullKey, serialized);
      }
      return next;
    });
    // Notify OTHER hook instances (same tab) after state commit.
    // Carry the serialized value in the event — localStorage writes are
    // debounced (300ms), so receivers that re-read storage would get stale
    // data and clobber the new value.
    requestAnimationFrame(() => {
      window.dispatchEvent(new CustomEvent('beast:state-sync', {
        detail: { key: fullKey, source: instanceId.current, value: serialized },
      }));
    });
  }, [fullKey]);

  return [value, setValue];
}

/**
 * Persisted Set — for things like expanded directories.
 */
export function usePersistedSet(key: string, defaultValue: string[]): [Set<string>, (path: string) => void, (value: Set<string>) => void] {
  const [arr, setArr] = usePersistedState<string[]>(key, defaultValue);
  const set = new Set(arr);

  const toggle = useCallback((path: string) => {
    setArr((prev) => {
      const s = new Set(prev);
      if (s.has(path)) s.delete(path);
      else s.add(path);
      return [...s];
    });
  }, [setArr]);

  const setFull = useCallback((value: Set<string>) => {
    setArr([...value]);
  }, [setArr]);

  return [set, toggle, setFull];
}
